"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  InputGroupAddon,
  InputGroupButton,
} from "@/components/ui/input-group";
import { Textarea } from "@/components/ui/textarea";
import { useAutoResize } from "@/hooks/use-auto-resize";
import { useIsMobile } from "@/hooks/use-media-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  containsFileDownloadUrl,
  rewriteFileLinksToPaths,
} from "@/lib/file-link-rewrite";
import type { ChatStatus, FileUIPart, SourceDocumentUIPart } from "ai";
import {
  ArrowUpIcon,
  CornerDownLeftIcon,
  ImageIcon,
  Monitor,
  PlusIcon,
  SquareIcon,
  XIcon,
} from "lucide-react";
import { nanoid } from "nanoid";
import type {
  ChangeEvent,
  ChangeEventHandler,
  ClipboardEventHandler,
  ComponentProps,
  FormEvent,
  FormEventHandler,
  HTMLAttributes,
  KeyboardEventHandler,
  PropsWithChildren,
  ReactNode,
  RefObject,
} from "react";
import {
  Children,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { isVoiceDictationShortcut } from "@/hooks/use-voice-dictation-shortcut";

// ============================================================================
// Helpers
// ============================================================================

const captureScreenshot = async (): Promise<File | null> => {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.getDisplayMedia
  ) {
    return null;
  }

  let stream: MediaStream | null = null;
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;

  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      audio: false,
      video: true,
    });

    video.srcObject = stream;

    // Video element uses callback-based API, wrapping in Promise is necessary
    // oxlint-disable-next-line eslint-plugin-promise(avoid-new)
    await new Promise<void>((resolve, reject) => {
      // oxlint-disable-next-line eslint-plugin-unicorn(prefer-add-event-listener)
      video.onloadedmetadata = () => resolve();
      // oxlint-disable-next-line eslint-plugin-unicorn(prefer-add-event-listener)
      video.onerror = () => reject(new Error("Failed to load screen stream"));
    });

    await video.play();

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      return null;
    }

    context.drawImage(video, 0, 0, width, height);
    // canvas.toBlob uses callback-based API, wrapping in Promise is necessary
    // oxlint-disable-next-line eslint-plugin-promise(avoid-new)
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/png");
    });
    if (!blob) {
      return null;
    }

    const timestamp = new Date()
      .toISOString()
      .replaceAll(/[:.]/g, "-")
      .replace("T", "_")
      .replace("Z", "");

    return new File([blob], `screenshot-${timestamp}.png`, {
      lastModified: Date.now(),
      type: "image/png",
    });
  } finally {
    if (stream) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
    }
    video.pause();
    video.srcObject = null;
  }
};

// ============================================================================
// Provider Context & Types
// ============================================================================

export interface AttachmentsContext {
  files: (FileUIPart & { id: string; rawFile?: File })[];
  add: (files: File[] | FileList) => void;
  /** Restore file parts from a failed/backgrounded send with fresh blob URLs. */
  restore: (files: (FileUIPart & { rawFile?: File })[]) => void;
  remove: (id: string) => void;
  clear: () => void;
  openFileDialog: () => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
}

export interface TextInputContext {
  value: string;
  setInput: (v: string) => void;
  clear: () => void;
}

export interface PromptInputControllerProps {
  textInput: TextInputContext;
  attachments: AttachmentsContext;
  /** INTERNAL: Allows PromptInput to register its file textInput + "open" callback */
  __registerFileInput: (
    ref: RefObject<HTMLInputElement | null>,
    open: () => void
  ) => void;
}

const PromptInputController = createContext<PromptInputControllerProps | null>(
  null
);
const ProviderAttachmentsContext = createContext<AttachmentsContext | null>(
  null
);

export const usePromptInputController = () => {
  const ctx = useContext(PromptInputController);
  if (!ctx) {
    throw new Error(
      "Wrap your component inside <PromptInputProvider> to use usePromptInputController()."
    );
  }
  return ctx;
};

// Optional variant (does NOT throw). Useful for dual-mode components like
// PromptInputSubmit, which needs to read the controller to auto-disable when
// the input is empty but must still work when no provider is present.
export const useOptionalPromptInputController = () =>
  useContext(PromptInputController);

export const useProviderAttachments = () => {
  const ctx = useContext(ProviderAttachmentsContext);
  if (!ctx) {
    throw new Error(
      "Wrap your component inside <PromptInputProvider> to use useProviderAttachments()."
    );
  }
  return ctx;
};

const useOptionalProviderAttachments = () =>
  useContext(ProviderAttachmentsContext);

export type PromptInputProviderProps = PropsWithChildren<{
  initialInput?: string;
  /** File parts to rehydrate when a failed send returns as a draft. */
  initialAttachments?: (FileUIPart & { rawFile?: File })[];
}>;

type PromptInputFile = FileUIPart & { rawFile?: File };

function withAttachmentId(file: PromptInputFile): PromptInputFile & { id: string } {
  const rawFile = file.rawFile;
  const url =
    typeof File !== "undefined" && rawFile instanceof File
      ? URL.createObjectURL(rawFile)
      : file.url;
  return {
    ...file,
    id: nanoid(),
    url,
  };
}

/**
 * Optional global provider that lifts PromptInput state outside of PromptInput.
 * If you don't use it, PromptInput stays fully self-managed.
 */
export const PromptInputProvider = ({
  initialInput: initialTextInput = "",
  initialAttachments = [],
  children,
}: PromptInputProviderProps) => {
  // ----- textInput state
  const [textInput, setTextInput] = useState(initialTextInput);
  const clearInput = useCallback(() => setTextInput(""), []);

  // ----- attachments state (global when wrapped)
  const [attachmentFiles, setAttachmentFiles] = useState<
    (FileUIPart & { id: string })[]
  >(() => initialAttachments.map(withAttachmentId));
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // oxlint-disable-next-line eslint(no-empty-function)
  const openRef = useRef<() => void>(() => {});

  const add = useCallback((files: File[] | FileList) => {
    const incoming = [...files];
    if (incoming.length === 0) {
      return;
    }

    setAttachmentFiles((prev) => [
      ...prev,
      ...incoming.map((file) => ({
        filename: file.name,
        id: nanoid(),
        mediaType: file.type,
        type: "file" as const,
        url: URL.createObjectURL(file),
        // Carry the raw File object through so uploadFilesToServer can
        // use it directly instead of fetch(blob:...) — which is blocked
        // by Content-Security-Policy on some hosts (e.g. catchall).
        rawFile: file,
      })),
    ]);
  }, []);

  const restore = useCallback((files: PromptInputFile[]) => {
    if (files.length === 0) return;
    setAttachmentFiles((prev) => [
      ...prev,
      ...files.map(withAttachmentId),
    ]);
  }, []);

  const remove = useCallback((id: string) => {
    setAttachmentFiles((prev) => {
      const found = prev.find((f) => f.id === id);
      if (found?.url) {
        URL.revokeObjectURL(found.url);
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setAttachmentFiles((prev) => {
      for (const f of prev) {
        if (f.url) {
          URL.revokeObjectURL(f.url);
        }
      }
      return [];
    });
  }, []);

  // Keep a ref to attachments for cleanup on unmount (avoids stale closure)
  const attachmentsRef = useRef(attachmentFiles);

  useEffect(() => {
    attachmentsRef.current = attachmentFiles;
  }, [attachmentFiles]);

  // Cleanup blob URLs on unmount to prevent memory leaks
  useEffect(
    () => () => {
      for (const f of attachmentsRef.current) {
        if (f.url) {
          URL.revokeObjectURL(f.url);
        }
      }
    },
    []
  );

  const openFileDialog = useCallback(() => {
    openRef.current?.();
  }, []);

  const attachments = useMemo<AttachmentsContext>(
    () => ({
      add,
      clear,
      fileInputRef,
      files: attachmentFiles,
      openFileDialog,
      remove,
      restore,
    }),
    [attachmentFiles, add, remove, clear, openFileDialog, restore]
  );

  const __registerFileInput = useCallback(
    (ref: RefObject<HTMLInputElement | null>, open: () => void) => {
      fileInputRef.current = ref.current;
      openRef.current = open;
    },
    []
  );

  const controller = useMemo<PromptInputControllerProps>(
    () => ({
      __registerFileInput,
      attachments,
      textInput: {
        clear: clearInput,
        setInput: setTextInput,
        value: textInput,
      },
    }),
    [textInput, clearInput, attachments, __registerFileInput]
  );

  return (
    <PromptInputController.Provider value={controller}>
      <ProviderAttachmentsContext.Provider value={attachments}>
        {children}
      </ProviderAttachmentsContext.Provider>
    </PromptInputController.Provider>
  );
};

// ============================================================================
// Component Context & Hooks
// ============================================================================

const LocalAttachmentsContext = createContext<AttachmentsContext | null>(null);

export const usePromptInputAttachments = () => {
  // Prefer local context (inside PromptInput) as it has validation, fall back to provider
  const provider = useOptionalProviderAttachments();
  const local = useContext(LocalAttachmentsContext);
  const context = local ?? provider;
  if (!context) {
    throw new Error(
      "usePromptInputAttachments must be used within a PromptInput or PromptInputProvider"
    );
  }
  return context;
};

// ============================================================================
// Referenced Sources (Local to PromptInput)
// ============================================================================

export interface ReferencedSourcesContext {
  sources: (SourceDocumentUIPart & { id: string })[];
  add: (sources: SourceDocumentUIPart[] | SourceDocumentUIPart) => void;
  remove: (id: string) => void;
  clear: () => void;
}

export const LocalReferencedSourcesContext =
  createContext<ReferencedSourcesContext | null>(null);

export const usePromptInputReferencedSources = () => {
  const ctx = useContext(LocalReferencedSourcesContext);
  if (!ctx) {
    throw new Error(
      "usePromptInputReferencedSources must be used within a LocalReferencedSourcesContext.Provider"
    );
  }
  return ctx;
};

export type PromptInputActionAddAttachmentsProps = ComponentProps<
  typeof DropdownMenuItem
> & {
  label?: string;
};

export const PromptInputActionAddAttachments = ({
  label,
  ...props
}: PromptInputActionAddAttachmentsProps) => {
  const { t } = useTranslation("chat");
  const attachments = usePromptInputAttachments();

  const handleSelect = useCallback(
    (e: Event) => {
      e.preventDefault();
      attachments.openFileDialog();
    },
    [attachments]
  );

  return (
    <DropdownMenuItem {...props} onSelect={handleSelect}>
      <ImageIcon className="mr-2 size-4" /> {label ?? t("input.addPhotosOrFiles")}
    </DropdownMenuItem>
  );
};

export type PromptInputActionAddScreenshotProps = ComponentProps<
  typeof DropdownMenuItem
> & {
  label?: string;
};

export const PromptInputActionAddScreenshot = ({
  label,
  onSelect,
  ...props
}: PromptInputActionAddScreenshotProps) => {
  const { t } = useTranslation("chat");
  const attachments = usePromptInputAttachments();

  const handleSelect = useCallback(
    async (event: Event) => {
      onSelect?.(event);
      if (event.defaultPrevented) {
        return;
      }

      try {
        const screenshot = await captureScreenshot();
        if (screenshot) {
          attachments.add([screenshot]);
        }
      } catch (error) {
        if (
          error instanceof DOMException &&
          (error.name === "NotAllowedError" || error.name === "AbortError")
        ) {
          return;
        }
        throw error;
      }
    },
    [onSelect, attachments]
  );

  return (
    <DropdownMenuItem {...props} onSelect={handleSelect}>
      <Monitor className="mr-2 size-4" />
      {label ?? t("input.takeScreenshot")}
    </DropdownMenuItem>
  );
};

export interface PromptInputMessage {
  text: string;
  /** File parts with an optional `rawFile` carrying the original `File`
   * object, so the upload path can use it directly without fetching a
   * `blob:` URL (which CSP may block). */
  files: (FileUIPart & { rawFile?: File })[];
}

export type PromptInputProps = Omit<
  HTMLAttributes<HTMLFormElement>,
  "onSubmit" | "onError"
> & {
  // e.g., "image/*" or leave undefined for any
  accept?: string;
  multiple?: boolean;
  // When true, accepts drops anywhere on document. Default false (opt-in).
  globalDrop?: boolean;
  // Render a hidden input with given name and keep it in sync for native form posts. Default false.
  syncHiddenInput?: boolean;
  // Minimal constraints
  maxFiles?: number;
  // bytes
  maxFileSize?: number;
  onError?: (err: {
    code: "max_files" | "max_file_size" | "accept";
    message: string;
  }) => void;
  onSubmit: (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>
  ) => void | Promise<void>;
};

export const PromptInput = ({
  className,
  accept,
  multiple,
  globalDrop,
  syncHiddenInput,
  maxFiles,
  maxFileSize,
  onError,
  onSubmit,
  children,
  ...props
}: PromptInputProps) => {
  const { t } = useTranslation();
  // Try to use a provider controller if present
  const controller = useOptionalPromptInputController();
  const usingProvider = !!controller;

  // Refs
  const inputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // ----- Local attachments (only used when no provider)
  const [items, setItems] = useState<(FileUIPart & { id: string })[]>([]);
  const files = usingProvider ? controller.attachments.files : items;

  // ----- Local referenced sources (always local to PromptInput)
  const [referencedSources, setReferencedSources] = useState<
    (SourceDocumentUIPart & { id: string })[]
  >([]);

  // Keep a ref to files for cleanup on unmount (avoids stale closure)
  const filesRef = useRef(files);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const openFileDialogLocal = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const matchesAccept = useCallback(
    (f: File) => {
      if (!accept || accept.trim() === "") {
        return true;
      }

      const patterns = accept
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      return patterns.some((pattern) => {
        if (pattern.endsWith("/*")) {
          // e.g: image/* -> image/
          const prefix = pattern.slice(0, -1);
          return f.type.startsWith(prefix);
        }
        return f.type === pattern;
      });
    },
    [accept]
  );

  const addLocal = useCallback(
    (fileList: File[] | FileList) => {
      const incoming = [...fileList];
      const accepted = incoming.filter((f) => matchesAccept(f));
      if (incoming.length && accepted.length === 0) {
        onError?.({
          code: "accept",
          message: t("input.errors.acceptedTypes"),
        });
        return;
      }
      const withinSize = (f: File) =>
        maxFileSize ? f.size <= maxFileSize : true;
      const sized = accepted.filter(withinSize);
      if (accepted.length > 0 && sized.length === 0) {
        onError?.({
          code: "max_file_size",
          message: t("input.errors.maximumSize"),
        });
        return;
      }

      setItems((prev) => {
        const capacity =
          typeof maxFiles === "number"
            ? Math.max(0, maxFiles - prev.length)
            : undefined;
        const capped =
          typeof capacity === "number" ? sized.slice(0, capacity) : sized;
        if (typeof capacity === "number" && sized.length > capacity) {
          onError?.({
            code: "max_files",
            message: t("input.errors.tooManyFiles"),
          });
        }
        const next: (FileUIPart & { id: string; rawFile?: File })[] = [];
        for (const file of capped) {
          next.push({
            filename: file.name,
            id: nanoid(),
            mediaType: file.type,
            type: "file",
            url: URL.createObjectURL(file),
            // Carry the raw File object through so uploadFilesToServer can
            // use it directly instead of fetch(blob:...) — which is blocked
            // by Content-Security-Policy on some hosts (e.g. catchall).
            rawFile: file,
          });
        }
        return [...prev, ...next];
      });
    },
    [matchesAccept, maxFiles, maxFileSize, onError, t]
  );

  const removeLocal = useCallback(
    (id: string) =>
      setItems((prev) => {
        const found = prev.find((file) => file.id === id);
        if (found?.url) {
          URL.revokeObjectURL(found.url);
        }
        return prev.filter((file) => file.id !== id);
      }),
    []
  );

  const restoreLocal = useCallback((incoming: PromptInputFile[]) => {
    if (incoming.length === 0) return;
    setItems((prev) => [
      ...prev,
      ...incoming.map(withAttachmentId),
    ]);
  }, []);

  // Wrapper that validates files before calling provider's add
  const addWithProviderValidation = useCallback(
    (fileList: File[] | FileList) => {
      const incoming = [...fileList];
      const accepted = incoming.filter((f) => matchesAccept(f));
      if (incoming.length && accepted.length === 0) {
        onError?.({
          code: "accept",
          message: t("input.errors.acceptedTypes"),
        });
        return;
      }
      const withinSize = (f: File) =>
        maxFileSize ? f.size <= maxFileSize : true;
      const sized = accepted.filter(withinSize);
      if (accepted.length > 0 && sized.length === 0) {
        onError?.({
          code: "max_file_size",
          message: t("input.errors.maximumSize"),
        });
        return;
      }

      const currentCount = files.length;
      const capacity =
        typeof maxFiles === "number"
          ? Math.max(0, maxFiles - currentCount)
          : undefined;
      const capped =
        typeof capacity === "number" ? sized.slice(0, capacity) : sized;
      if (typeof capacity === "number" && sized.length > capacity) {
        onError?.({
          code: "max_files",
          message: t("input.errors.tooManyFiles"),
        });
      }

      if (capped.length > 0) {
        controller?.attachments.add(capped);
      }
    },
    [matchesAccept, maxFileSize, maxFiles, onError, files.length, controller, t]
  );

  const clearAttachments = useCallback(
    () =>
      usingProvider
        ? controller?.attachments.clear()
        : setItems((prev) => {
            for (const file of prev) {
              if (file.url) {
                URL.revokeObjectURL(file.url);
              }
            }
            return [];
          }),
    [usingProvider, controller]
  );

  const clearReferencedSources = useCallback(
    () => setReferencedSources([]),
    []
  );

  const add = usingProvider ? addWithProviderValidation : addLocal;
  const remove = usingProvider ? controller.attachments.remove : removeLocal;
  const openFileDialog = usingProvider
    ? controller.attachments.openFileDialog
    : openFileDialogLocal;

  const clear = useCallback(() => {
    clearAttachments();
    clearReferencedSources();
  }, [clearAttachments, clearReferencedSources]);

  // Let provider know about our hidden file input so external menus can call openFileDialog()
  useEffect(() => {
    if (!usingProvider) {
      return;
    }
    controller.__registerFileInput(inputRef, () => inputRef.current?.click());
  }, [usingProvider, controller]);

  // Note: File input cannot be programmatically set for security reasons
  // The syncHiddenInput prop is no longer functional
  useEffect(() => {
    if (syncHiddenInput && inputRef.current && files.length === 0) {
      inputRef.current.value = "";
    }
  }, [files, syncHiddenInput]);

  // Attach drop handlers on nearest form and document (opt-in)
  useEffect(() => {
    const form = formRef.current;
    if (!form) {
      return;
    }
    if (globalDrop) {
      // when global drop is on, let the document-level handler own drops
      return;
    }

    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        add(e.dataTransfer.files);
      }
    };
    form.addEventListener("dragover", onDragOver);
    form.addEventListener("drop", onDrop);
    return () => {
      form.removeEventListener("dragover", onDragOver);
      form.removeEventListener("drop", onDrop);
    };
  }, [add, globalDrop]);

  useEffect(() => {
    if (!globalDrop) {
      return;
    }

    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        add(e.dataTransfer.files);
      }
    };
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
    };
  }, [add, globalDrop]);

  useEffect(
    () => () => {
      if (!usingProvider) {
        for (const f of filesRef.current) {
          if (f.url) {
            URL.revokeObjectURL(f.url);
          }
        }
      }
    },
    [usingProvider]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.currentTarget.files) {
        add(event.currentTarget.files);
      }
      // Reset input value to allow selecting files that were previously removed
      event.currentTarget.value = "";
    },
    [add]
  );

  const attachmentsCtx = useMemo<AttachmentsContext>(
    () => ({
      add,
      clear: clearAttachments,
      fileInputRef: inputRef,
      files: files.map((item) => ({ ...item, id: item.id })),
      openFileDialog,
      remove,
      restore: usingProvider ? controller.attachments.restore : restoreLocal,
    }),
    [
      files,
      add,
      remove,
      clearAttachments,
      openFileDialog,
      usingProvider,
      controller,
      restoreLocal,
    ]
  );

  const refsCtx = useMemo<ReferencedSourcesContext>(
    () => ({
      add: (incoming: SourceDocumentUIPart[] | SourceDocumentUIPart) => {
        const array = Array.isArray(incoming) ? incoming : [incoming];
        setReferencedSources((prev) => [
          ...prev,
          ...array.map((s) => ({ ...s, id: nanoid() })),
        ]);
      },
      clear: clearReferencedSources,
      remove: (id: string) => {
        setReferencedSources((prev) => prev.filter((s) => s.id !== id));
      },
      sources: referencedSources,
    }),
    [referencedSources, clearReferencedSources]
  );

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (event) => {
      event.preventDefault();

      const form = event.currentTarget;
      const text = usingProvider
        ? controller.textInput.value
        : (() => {
            const formData = new FormData(form);
            return (formData.get("message") as string) || "";
          })();

      // ── Empty-message guard ──────────────────────────────────────────────
      // Prevent sending a message that has no text AND no attachments.
      // This blocks whitespace-only Enter presses (e.g. accidental submit on
      // an empty textarea) and double-clicks on the submit button when nothing
      // has been typed. A message with attachments but no text is still valid.
      if (text.trim() === "" && files.length === 0) {
        return;
      }

      // Reset form immediately after capturing text to avoid race condition
      // where user input during async operations would be lost
      if (!usingProvider) {
        form.reset();
      }

      // Clear the TEXT input immediately on submit so the textarea is empty
      // while the message is being processed.  Attachments (chips) are NOT
      // cleared here — they are deferred until after onSubmit resolves, so
      // they stay visible during file uploads (where the consumer awaits
      // the multipart upload before sending the chat message).
      //
      // If we cleared everything up front (text + attachments), the chips
      // would vanish before the upload started, giving no visual feedback.
      // If we deferred both, the text would linger in the textarea until the
      // entire model response finished streaming — which looked broken.
      if (usingProvider) {
        controller.textInput.clear();
      }
      // Clear referenced sources immediately (they're display-only, not
      // needed during the upload or streaming phase).
      clearReferencedSources();

      try {
        // Pass file parts directly to onSubmit — no base64 conversion.
        //
        // Previously, blob: URLs were converted to data: URLs (base64) so
        // the file content could be embedded in the /chat/web JSON body.
        // Now that the UI uploads files via the multipart endpoint first,
        // we pass the raw File object alongside the blob: URL so
        // uploadFilesToServer can use it directly — no fetch(blob:...)
        // which may be blocked by Content-Security-Policy.
        //
        // Strip the `id` field (internal to PromptInput) before passing.
        const fileParts = files.map(({ id: _id, ...item }) => item);

        const result = onSubmit({ files: fileParts, text }, event);

        // Handle both sync and async onSubmit
        if (result instanceof Promise) {
          try {
            await result;
            // Clear attachments only after the promise resolves — the
            // consumer (ChatInterface) awaits sendMessage, which includes
            // the file upload.  Keeping chips visible during the upload
            // gives the user visual feedback that the files are in flight.
            clearAttachments();
          } catch {
            // Don't clear attachments on error - user may want to retry
          }
        } else {
          // Sync onSubmit completed — clear attachments now.
          clearAttachments();
        }
      } catch {
        // Don't clear attachments on error - user may want to retry
      }
    },
    [usingProvider, controller, files, onSubmit, clearAttachments, clearReferencedSources]
  );

  // Render with or without local provider
  const inner = (
    <>
      <input
        accept={accept}
        aria-label={t("input.uploadFiles")}
        className="hidden"
        multiple={multiple}
        onChange={handleChange}
        ref={inputRef}
        title={t("input.uploadFiles")}
        type="file"
      />
      <form
        className={cn("w-full", className)}
        onSubmit={handleSubmit}
        ref={formRef}
        {...props}
      >
        {children}
      </form>
    </>
  );

  const withReferencedSources = (
    <LocalReferencedSourcesContext.Provider value={refsCtx}>
      {inner}
    </LocalReferencedSourcesContext.Provider>
  );

  // Always provide LocalAttachmentsContext so children get validated add function
  return (
    <LocalAttachmentsContext.Provider value={attachmentsCtx}>
      {withReferencedSources}
    </LocalAttachmentsContext.Provider>
  );
};

export type PromptInputBodyProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputBody = ({
  className,
  ...props
}: PromptInputBodyProps) => (
  <div className={cn("contents", className)} {...props} />
);

export type PromptInputTextareaProps = ComponentProps<typeof Textarea> & {
  /**
   * Controls the desktop send shortcut. Enter is the default; Shift+Enter
   * remains the multiline escape hatch.
   */
  submitShortcut?: "enter" | "ctrl_enter";
  /**
   * Focus the textarea whenever this value changes (and on initial mount).
   * Pass the current session/thread id to auto-focus when switching
   * conversations. Pass `undefined` to opt out of auto-focus entirely.
   */
  autoFocusKey?: string;
  /**
   * Fired when the textarea's surrounding action layout should transition
   * between horizontal and stacked. A non-empty draft emits `true` when it
   * first grows beyond one line, then stays `true` until that draft is
   * cleared. This prevents the surrounding layout from feeding back into
   * the textarea width and making the buttons flicker.
   */
  onMultilineChange?: (multiline: boolean) => void;
  /** Starts voice dictation for this composer on Cmd/Ctrl+/. */
  onVoiceDictationShortcut?: () => void;
};

export const PromptInputTextarea = ({
  onChange,
  onKeyDown,
  className,
  placeholder,
  autoFocusKey,
  onMultilineChange,
  onVoiceDictationShortcut,
  submitShortcut = "enter",
  ...props
}: PromptInputTextareaProps) => {
  const { t } = useTranslation("chat");
  const controller = useOptionalPromptInputController();
  const attachments = usePromptInputAttachments();
  const [isComposing, setIsComposing] = useState(false);
  // Touch devices always keep Enter for multiline input; the on-screen send
  // button is the only way to submit a message.
  const isMobile = useIsMobile();

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      // Call the external onKeyDown handler first
      onKeyDown?.(e);

      // If the external handler prevented default, don't run internal logic
      if (e.defaultPrevented) {
        return;
      }

      if (onVoiceDictationShortcut && isVoiceDictationShortcut(e)) {
        e.preventDefault();
        onVoiceDictationShortcut();
        return;
      }

      if (e.key === "Enter") {
        if (isComposing || e.nativeEvent.isComposing) {
          return;
        }
        // On mobile, never intercept Enter so the browser inserts a newline.
        if (isMobile) {
          return;
        }
        // In Enter mode Shift+Enter remains the multiline escape hatch.
        // Ctrl/Cmd+Enter still sends in either mode, which preserves the
        // familiar desktop shortcut for people who use it habitually.
        const shouldSubmit = submitShortcut === "enter"
          ? !e.shiftKey
          : e.ctrlKey || e.metaKey;
        if (!shouldSubmit) {
          return;
        }
        e.preventDefault();

        // Respect the send button's disabled state. Consumers without a
        // mid-run send path may replace it with a stop button, leaving no
        // submit button for this shortcut to activate.
        const { form } = e.currentTarget;
        const submitButton = form?.querySelector(
          'button[type="submit"]'
        ) as HTMLButtonElement | null;
        if (!submitButton || submitButton.disabled) {
          return;
        }

        form?.requestSubmit();
      }

      // Remove last attachment when Backspace is pressed and textarea is empty
      if (
        e.key === "Backspace" &&
        e.currentTarget.value === "" &&
        attachments.files.length > 0
      ) {
        e.preventDefault();
        const lastAttachment = attachments.files.at(-1);
        if (lastAttachment) {
          attachments.remove(lastAttachment.id);
        }
      }
    },
    [onKeyDown, isComposing, attachments, isMobile, onVoiceDictationShortcut, submitShortcut]
  );

  const handlePaste: ClipboardEventHandler<HTMLTextAreaElement> = useCallback(
    async (event) => {
      const items = event.clipboardData?.items;

      if (!items) {
        return;
      }

      // ── File-image paste (screenshots, dragged images) ────────────────
      // If the clipboard contains image files, hand them to the attachment
      // pipeline and skip text insertion entirely.
      const files: File[] = [];

      for (const item of items) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        event.preventDefault();
        attachments.add(files);
        return;
      }

      // ── File-download URL rewrite ──────────────────────────────────────
      // When a user copies a signed file-download URL (e.g. from a previous
      // assistant message or attachment pill) and pastes it into the chat
      // input, decode the JWT payload and replace the URL with the bare file
      // path. v1 tokens carry ``fp``; v2 tokens are resolved through the
      // token-gated server endpoint. The agent can't use a
      // ``https://chat-bridge.l-9.net/files/d/{jwt}`` URL with its file
      // tools — it needs the actual path. Doing this on paste (rather than
      // on submit) means the user sees the path immediately, can edit it,
      // and what they see is what gets sent.
      const pastedText = event.clipboardData?.getData("text/plain") ?? "";
      if (!containsFileDownloadUrl(pastedText)) {
        return;
      }

      // Prevent the browser's default insertion before awaiting a v2 resolve
      // request. If the endpoint is unavailable, the original URL is inserted
      // below unchanged instead of being lost or inserted twice.
      event.preventDefault();
      const textarea = event.currentTarget;
      let rewritten = pastedText;
      let paths: string[] = [];
      try {
        const result = await rewriteFileLinksToPaths(pastedText);
        rewritten = result.text;
        paths = result.paths;
      } catch {
        // Keep the signed URL intact if an unexpected client-side failure
        // occurs while resolving it.
      }
      const insertText = paths.length > 0 ? rewritten : pastedText;

      // Insert the rewritten text at the cursor position. Works for both
      // controlled (provider) and uncontrolled modes.
      if (controller) {
        // Controlled mode: update via the controller's setInput.
        const before = controller.textInput.value.slice(
          0,
          textarea.selectionStart,
        );
        const after = controller.textInput.value.slice(
          textarea.selectionEnd,
        );
        controller.textInput.setInput(before + insertText + after);
        // Restore cursor position after React re-renders. Place it right
        // after the inserted text.
        requestAnimationFrame(() => {
          const pos = before.length + insertText.length;
          textarea.focus();
          textarea.setSelectionRange(pos, pos);
        });
      } else {
        // Uncontrolled mode: use execCommand for undo support, falling
        // back to manual insertion.
        const ok = document.execCommand("insertText", false, insertText);
        if (!ok) {
          // Manual fallback for browsers without execCommand support.
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const before = textarea.value.slice(0, start);
          const after = textarea.value.slice(end);
          textarea.value = before + insertText + after;
          const pos = before.length + insertText.length;
          textarea.setSelectionRange(pos, pos);
          // Dispatch an input event so React's onChange fires.
          textarea.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    },
    [attachments, controller],
  );

  const handleCompositionEnd = useCallback(() => setIsComposing(false), []);
  const handleCompositionStart = useCallback(() => setIsComposing(true), []);

  const { ref: resizeRef, resizeForValueChange } = useAutoResize(10, 1);

  // Auto-focus the textarea when `autoFocusKey` changes (and on first mount).
  // Used to focus the input when switching threads and on initial page load.
  // On mobile we skip this to avoid popping up the on-screen keyboard
  // unexpectedly — the user can tap the textarea when they're ready to type.
  useEffect(() => {
    if (autoFocusKey === undefined) return;
    if (isMobile) return;
    const el = resizeRef.current;
    if (!el) return;
    // Defer one frame so the DOM is committed (especially after a thread
    // switch where the message list is still rendering).
    requestAnimationFrame(() => resizeRef.current?.focus());
  }, [autoFocusKey, isMobile, resizeRef]);

  // In controlled mode (provider or value prop), clearing the input after submit
  // happens via setTextInput(""), which updates the value without firing
  // onChange or a form "reset" event — so the textarea would stay expanded
  // until the next keystroke. Watch the controlled value and re-measure
  // synchronously before paint so the textarea shrinks immediately.
  const controlledValue = controller ? controller.textInput.value : props.value?.toString();
  useLayoutEffect(() => {
    resizeForValueChange();
  }, [controlledValue, resizeForValueChange]);

  // Keep the action-layout signal sticky for a non-empty draft. When the
  // buttons stack, the textarea gets wider, which can make the same text fit
  // back onto one physical line. Reporting that transient one-line height as
  // `false` would immediately unstack the buttons and recreate the narrow
  // width that caused the wrap.
  const promptActionsStackedRef = useRef(false);
  const updatePromptActionsLayout = useCallback(
    (isPhysicallyMultiline: boolean, value: string, notifyEmpty = false) => {
      const hasDraft = value.trim() !== "";

      if (!hasDraft) {
        const wasStacked = promptActionsStackedRef.current;
        promptActionsStackedRef.current = false;
        if ((wasStacked || notifyEmpty) && onMultilineChange) {
          onMultilineChange(false);
        }
        return;
      }

      if (isPhysicallyMultiline && !promptActionsStackedRef.current) {
        promptActionsStackedRef.current = true;
        onMultilineChange?.(true);
      }
    },
    [onMultilineChange],
  );

  // ── Empty-input notification ─────────────────────────────────────────
  // When the controlled value is cleared (e.g. after a successful submit
  // or when the parent resets the draft), the textarea is logically a single
  // line again. Notify the consumer immediately so surrounding layout can
  // revert — even if the ResizeObserver has not run yet after a programmatic
  // clear.
  const prevControlledValueRef = useRef(controlledValue);
  useLayoutEffect(() => {
    const prev = prevControlledValueRef.current;
    const current = controlledValue;
    prevControlledValueRef.current = current;
    if (prev && prev.trim() !== "" && current?.trim() === "") {
      // Notify even when the textarea never overflowed. This preserves the
      // existing empty-input signal for consumers and resets a stacked layout
      // before the next paint after submit.
      updatePromptActionsLayout(false, current ?? "", true);
    }
  }, [controlledValue, updatePromptActionsLayout]);

  // ── Multi-line detection ────────────────────────────────────────────
  // A ResizeObserver catches every height change — typing, paste, submit
  // clearing, and programmatic setInput. Compare against a one-line height
  // derived from the textarea's typography, not against the first observed
  // height: the first observation may already be a wrapped draft, or may
  // happen after the action buttons have changed the textarea width.
  useLayoutEffect(() => {
    const el = resizeRef.current;
    if (!el || !onMultilineChange) return;

    const ro = new ResizeObserver(() => {
      // Re-read responsive typography/control tokens when the viewport changes.
      // offsetHeight includes borders, so compare border-box heights throughout.
      const styles = getComputedStyle(el);
      const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
      const verticalPadding =
        (Number.parseFloat(styles.paddingTop) || 0) +
        (Number.parseFloat(styles.paddingBottom) || 0);
      const borderHeight =
        (Number.parseFloat(styles.borderTopWidth) || 0) +
        (Number.parseFloat(styles.borderBottomWidth) || 0);
      const minHeight = Number.parseFloat(styles.minHeight) || 0;
      const oneLineHeight = Math.max(minHeight, lineHeight + verticalPadding + borderHeight);
      // A half-line buffer avoids rounding noise while still detecting a second line.
      const multilineThreshold = oneLineHeight + lineHeight / 2;
      updatePromptActionsLayout(el.offsetHeight > multilineThreshold, el.value);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onMultilineChange, resizeRef, updatePromptActionsLayout]);

  const controlledProps = controller
    ? {
        onChange: (e: ChangeEvent<HTMLTextAreaElement>) => {
          controller.textInput.setInput(e.currentTarget.value);
          onChange?.(e);
          // No resize here: the useLayoutEffect above re-measures whenever the
          // controlled value changes, so calling it here too would duplicate
          // the measurement for every keystroke.
        },
        value: controller.textInput.value,
      }
    : {
        onChange: (e: ChangeEvent<HTMLTextAreaElement>) => {
          onChange?.(e);
          // Controlled values are measured after React commits them.
          if (props.value === undefined) resizeForValueChange();
        },
      };

  return (
    <Textarea
      ref={resizeRef}
      // Match the one-row minimum before the initial autosize measurement.
      rows={1}
      // Height (incl. max-height + scrollbar) is managed by useAutoResize via
      // inline styles. Don't set a CSS max-h here or it conflicts with the
      // hook's cap and clips content without showing a scrollbar.
      className={cn("flex-1 min-h-9 resize-none", className)}
      name="message"
      // On mobile, Enter inserts a newline (see handleKeyDown). Tell the
      // on-screen keyboard to show a return key instead of "send".
      enterKeyHint={isMobile ? "enter" : "send"}
      onCompositionEnd={handleCompositionEnd}
      onCompositionStart={handleCompositionStart}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      placeholder={placeholder ?? t("input.defaultPlaceholder")}
      {...props}
      {...controlledProps}
    />
  );
};

export type PromptInputHeaderProps = Omit<
  ComponentProps<typeof InputGroupAddon>,
  "align"
>;

export const PromptInputHeader = ({
  className,
  ...props
}: PromptInputHeaderProps) => (
  <InputGroupAddon
    align="block-end"
    className={cn("order-first flex-wrap gap-1", className)}
    {...props}
  />
);

export type PromptInputFooterProps = Omit<
  ComponentProps<typeof InputGroupAddon>,
  "align"
>;

export const PromptInputFooter = ({
  className,
  ...props
}: PromptInputFooterProps) => (
  <InputGroupAddon
    align="block-end"
    className={cn("justify-between gap-1", className)}
    {...props}
  />
);

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = ({
  className,
  ...props
}: PromptInputToolsProps) => (
  <div
    className={cn("flex min-w-0 items-center gap-1", className)}
    {...props}
  />
);

export type PromptInputButtonTooltip =
  | string
  | {
      content: ReactNode;
      shortcut?: string;
      side?: ComponentProps<typeof TooltipContent>["side"];
    };

export type PromptInputButtonProps = ComponentProps<typeof InputGroupButton> & {
  tooltip?: PromptInputButtonTooltip;
};

export const PromptInputButton = ({
  variant = "ghost",
  className,
  size,
  tooltip,
  ...props
}: PromptInputButtonProps) => {
  const newSize =
    size ?? (Children.count(props.children) > 1 ? "sm" : "icon-sm");

  const button = (
    <InputGroupButton
      className={cn(className)}
      size={newSize}
      type="button"
      variant={variant}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  const tooltipContent =
    typeof tooltip === "string" ? tooltip : tooltip.content;
  const shortcut = typeof tooltip === "string" ? undefined : tooltip.shortcut;
  const side = typeof tooltip === "string" ? "top" : (tooltip.side ?? "top");

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side={side}>
          {tooltipContent}
          {shortcut && (
            <span className="ml-2 text-muted-foreground">{shortcut}</span>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export type PromptInputActionMenuProps = ComponentProps<typeof DropdownMenu>;
export const PromptInputActionMenu = (props: PromptInputActionMenuProps) => (
  <DropdownMenu {...props} />
);

export type PromptInputActionMenuTriggerProps = PromptInputButtonProps;

export const PromptInputActionMenuTrigger = ({
  className,
  children,
  ...props
}: PromptInputActionMenuTriggerProps) => (
  <DropdownMenuTrigger asChild>
    <PromptInputButton className={className} {...props}>
      {children ?? <PlusIcon className="size-4" />}
    </PromptInputButton>
  </DropdownMenuTrigger>
);

export type PromptInputActionMenuContentProps = ComponentProps<
  typeof DropdownMenuContent
>;
export const PromptInputActionMenuContent = ({
  className,
  ...props
}: PromptInputActionMenuContentProps) => (
  <DropdownMenuContent align="start" className={cn(className)} {...props} />
);

export type PromptInputActionMenuItemProps = ComponentProps<
  typeof DropdownMenuItem
>;
export const PromptInputActionMenuItem = ({
  className,
  ...props
}: PromptInputActionMenuItemProps) => (
  <DropdownMenuItem className={cn(className)} {...props} />
);

// Note: Actions that perform side-effects (like opening a file dialog)
// are provided in opt-in modules (e.g., prompt-input-attachments).

export type PromptInputSubmitProps = ComponentProps<typeof InputGroupButton> & {
  status?: ChatStatus;
  /** Show a spinner when files are being uploaded (before the chat message
   *  is sent).  ``ChatStatus`` has no "uploading" value, so this is a
   *  separate flag wired in by the consumer. */
  isUploading?: boolean;
  /** Show a spinner while a voice-note audio file is being transcribed
   *  (after upload, before the chat message is sent).  Like ``isUploading``,
   *  this is a separate flag because ``ChatStatus`` has no "transcribing"
   *  value — ``status`` is still ``"ready"`` during this phase. */
  isTranscribing?: boolean;
  onStop?: () => void;
  /**
   * When true, the button acts as a voice-note submit: the icon changes to
   * an arrow-up (matching the voice-note send affordance) and clicking it
   * calls ``onVoiceSubmit`` instead of submitting the text form.
   */
  isRecordingVoice?: boolean;
  /** Called when the user clicks the submit button while ``isRecordingVoice``
   *  is true.  The parent should stop the recording and send the voice note. */
  onVoiceSubmit?: () => void;
};

export const PromptInputSubmit = forwardRef<HTMLButtonElement, PromptInputSubmitProps>(({
  className,
  variant = "default",
  size = "icon-sm",
  status,
  isUploading,
  isTranscribing,
  isRecordingVoice,
  onVoiceSubmit,
  onStop,
  onClick,
  children,
  disabled,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const isGenerating = status === "submitted" || status === "streaming";
  const isStopButton = isGenerating && !!onStop;

  // Auto-disable when the input is empty and no attachments are present, so
  // Enter/Space/click can't trigger an empty submission. We read the optional
  // controller + attachments context so this works inside <PromptInputProvider>
  // without requiring the consumer to wire up `disabled` manually. When no
  // provider is present (self-managed <PromptInput>) we can't read the text
  // value here, so we rely on the handleSubmit guard in <PromptInput>.
  const controller = useOptionalPromptInputController();
  const attachments = useOptionalProviderAttachments();
  const hasText = (controller?.textInput.value ?? "").trim() !== "";
  const hasFiles = (attachments?.files.length ?? 0) > 0;
  const isEmpty = !hasText && !hasFiles;
  // Disable while uploading/transcribing (prevents double-submit) or when
  // the input is empty with no attachments. A stop button remains clickable
  // during a run even when the input is empty.
  // When recording a voice note, the button must always be enabled so the user
  // can send the recording even with no text/attachments.
  const autoDisabled =
    !!isUploading ||
    !!isTranscribing ||
    (!isStopButton && !isRecordingVoice && isEmpty);
  const isDisabled = disabled ?? autoDisabled;

  let Icon = <CornerDownLeftIcon className="size-4" />;

  // While recording a voice note, the submit button becomes the voice-note
  // send button — same arrow-up icon as the previous inline submit, so the
  // user sees an explicit signal that the button's meaning has changed.
  if (isRecordingVoice) {
    Icon = <ArrowUpIcon className="size-4" />;
  } else if (isUploading || isTranscribing) {
    Icon = <Spinner />;
  } else if (isStopButton && status === "submitted") {
    Icon = <Spinner />;
  } else if (isStopButton && status === "streaming") {
    Icon = <SquareIcon className="size-4" />;
  } else if (status === "error") {
    Icon = <XIcon className="size-4" />;
  }

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Ignore clicks during upload/transcription — the button is also
      // disabled, but this is a safety net for race conditions.
      if (isUploading || isTranscribing) {
        e.preventDefault();
        return;
      }
      // Voice-note submit takes priority over stop and normal text submit —
      // the recording must be sent first.
      if (isRecordingVoice && onVoiceSubmit) {
        e.preventDefault();
        onVoiceSubmit();
        return;
      }
      if (isStopButton) {
        e.preventDefault();
        onStop?.();
        return;
      }
      onClick?.(e);
    },
    [isStopButton, isUploading, isTranscribing, isRecordingVoice, onVoiceSubmit, onStop, onClick]
  );

  return (
    <InputGroupButton
      ref={ref}
      aria-label={
        isRecordingVoice
          ? t("voice.submit")
          : isUploading
            ? t("common:status.uploading")
            : isTranscribing
              ? t("common:status.transcribing")
              : isStopButton
                ? t("input.stop")
                : t("input.submit")
      }
      className={cn(className)}
      onClick={handleClick}
      size={size}
      // While recording, the button is NOT a form submit — it triggers
      // onVoiceSubmit (which stops the recording) instead.
      type={isRecordingVoice || isStopButton ? "button" : "submit"}
      variant={variant}
      disabled={isDisabled}
      data-testid={isRecordingVoice ? "voice-note-submit" : undefined}
      {...props}
    >
      {children ?? Icon}
    </InputGroupButton>
  );
});
PromptInputSubmit.displayName = "PromptInputSubmit";

export type PromptInputSelectProps = ComponentProps<typeof Select>;

export const PromptInputSelect = (props: PromptInputSelectProps) => (
  <Select {...props} />
);

export type PromptInputSelectTriggerProps = ComponentProps<
  typeof SelectTrigger
>;

export const PromptInputSelectTrigger = ({
  className,
  ...props
}: PromptInputSelectTriggerProps) => (
  <SelectTrigger
    className={cn(
      "border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors",
      "hover:bg-accent hover:text-foreground aria-expanded:bg-accent aria-expanded:text-foreground",
      className
    )}
    {...props}
  />
);

export type PromptInputSelectContentProps = ComponentProps<
  typeof SelectContent
>;

export const PromptInputSelectContent = ({
  className,
  ...props
}: PromptInputSelectContentProps) => (
  <SelectContent className={cn(className)} {...props} />
);

export type PromptInputSelectItemProps = ComponentProps<typeof SelectItem>;

export const PromptInputSelectItem = ({
  className,
  ...props
}: PromptInputSelectItemProps) => (
  <SelectItem className={cn(className)} {...props} />
);

export type PromptInputSelectValueProps = ComponentProps<typeof SelectValue>;

export const PromptInputSelectValue = ({
  className,
  ...props
}: PromptInputSelectValueProps) => (
  <SelectValue className={cn(className)} {...props} />
);

export type PromptInputHoverCardProps = ComponentProps<typeof HoverCard>;

export const PromptInputHoverCard = ({
  openDelay = 0,
  closeDelay = 0,
  ...props
}: PromptInputHoverCardProps) => (
  <HoverCard closeDelay={closeDelay} openDelay={openDelay} {...props} />
);

export type PromptInputHoverCardTriggerProps = ComponentProps<
  typeof HoverCardTrigger
>;

export const PromptInputHoverCardTrigger = (
  props: PromptInputHoverCardTriggerProps
) => <HoverCardTrigger {...props} />;

export type PromptInputHoverCardContentProps = ComponentProps<
  typeof HoverCardContent
>;

export const PromptInputHoverCardContent = ({
  align = "start",
  ...props
}: PromptInputHoverCardContentProps) => (
  <HoverCardContent align={align} {...props} />
);

export type PromptInputTabsListProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTabsList = ({
  className,
  ...props
}: PromptInputTabsListProps) => <div className={cn(className)} {...props} />;

export type PromptInputTabProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTab = ({
  className,
  ...props
}: PromptInputTabProps) => <div className={cn(className)} {...props} />;

export type PromptInputTabLabelProps = HTMLAttributes<HTMLHeadingElement>;

export const PromptInputTabLabel = ({
  className,
  ...props
}: PromptInputTabLabelProps) => (
  // Content provided via children in props
  // oxlint-disable-next-line eslint-plugin-jsx-a11y(heading-has-content)
  <h3
    className={cn(
      "mb-2 px-3 font-medium text-muted-foreground text-xs",
      className
    )}
    {...props}
  />
);

export type PromptInputTabBodyProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTabBody = ({
  className,
  ...props
}: PromptInputTabBodyProps) => (
  <div className={cn("space-y-1", className)} {...props} />
);

export type PromptInputTabItemProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTabItem = ({
  className,
  ...props
}: PromptInputTabItemProps) => (
  <div
    className={cn(
      "flex items-center gap-2 px-3 py-2 text-xs hover:bg-accent",
      className
    )}
    {...props}
  />
);

export type PromptInputCommandProps = ComponentProps<typeof Command>;

export const PromptInputCommand = ({
  className,
  ...props
}: PromptInputCommandProps) => <Command className={cn(className)} {...props} />;

export type PromptInputCommandInputProps = ComponentProps<typeof CommandInput>;

export const PromptInputCommandInput = ({
  className,
  ...props
}: PromptInputCommandInputProps) => (
  <CommandInput className={cn(className)} {...props} />
);

export type PromptInputCommandListProps = ComponentProps<typeof CommandList>;

export const PromptInputCommandList = ({
  className,
  ...props
}: PromptInputCommandListProps) => (
  <CommandList className={cn(className)} {...props} />
);

export type PromptInputCommandEmptyProps = ComponentProps<typeof CommandEmpty>;

export const PromptInputCommandEmpty = ({
  className,
  ...props
}: PromptInputCommandEmptyProps) => (
  <CommandEmpty className={cn(className)} {...props} />
);

export type PromptInputCommandGroupProps = ComponentProps<typeof CommandGroup>;

export const PromptInputCommandGroup = ({
  className,
  ...props
}: PromptInputCommandGroupProps) => (
  <CommandGroup className={cn(className)} {...props} />
);

export type PromptInputCommandItemProps = ComponentProps<typeof CommandItem>;

export const PromptInputCommandItem = ({
  className,
  ...props
}: PromptInputCommandItemProps) => (
  <CommandItem className={cn(className)} {...props} />
);

export type PromptInputCommandSeparatorProps = ComponentProps<
  typeof CommandSeparator
>;

export const PromptInputCommandSeparator = ({
  className,
  ...props
}: PromptInputCommandSeparatorProps) => (
  <CommandSeparator className={cn(className)} {...props} />
);
